import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatToDateTimeString } from "../helpers/dbDateFormat";
import ExcelUploadPreview from "./event-automation-excel";
import Multiselect from "multiselect-react-dropdown";
import GetUserLocationList from "../hooks/getUserLocationList";
import { setHours, setMinutes, isSameDay, addDays } from "date-fns";
import { enIN } from "date-fns/locale/en-IN";
import * as XLSX from "xlsx";
import getMobileNumbersByCity from "../hooks/getMobileNumbersByCity";
import CustomModal from "./modal";
import QRCode from "../vendor/qrcode-lib/browser";

const createPaymentDraft = (values = {}) => ({
  account_number: values.account_number || "",
  ifsc_code: values.ifsc_code || "",
  account_holder_name: values.account_holder_name || "",
  upi_id: values.upi_id || "",
});

const normalizePaymentDraft = (draft) => ({
  account_number: draft.account_number.trim(),
  ifsc_code: draft.ifsc_code.trim().toUpperCase(),
  account_holder_name: draft.account_holder_name.trim(),
  upi_id: draft.upi_id.trim(),
});

const hasBankPaymentDetails = (values = {}) =>
  Boolean(
    values.account_number?.trim() &&
      values.ifsc_code?.trim() &&
      values.account_holder_name?.trim(),
  );

const hasUpiPaymentDetails = (values = {}) => Boolean(values.upi_id?.trim());
const UPI_ID_REGEX = /^[\w.+-]+@[\w.-]+$/;

const buildOnlinePaymentDetails = (values = {}) => {
  const normalized = normalizePaymentDraft(values);

  return JSON.stringify({
    account_details: hasBankPaymentDetails(normalized)
      ? {
          account_number: normalized.account_number,
          ifsc_code: normalized.ifsc_code,
          holder_name: normalized.account_holder_name,
        }
      : null,
    upi_id: hasUpiPaymentDetails(normalized) ? normalized.upi_id : null,
  });
};

const buildUpiPaymentUri = ({ upiId, note }) => {
  if (!upiId) return "";

  const params = new URLSearchParams({
    pa: upiId,
    cu: "INR",
  });

  if (note) {
    params.set("tn", note);
  }

  return `upi://pay?${params.toString()}`;
};

const PaymentDetailsModal = ({
  show,
  initialValues,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState(createPaymentDraft(initialValues));
  const [error, setError] = useState("");

  useEffect(() => {
    if (show) {
      setDraft(createPaymentDraft(initialValues));
      setError("");
    }
  }, [show, initialValues]);

  const setField = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setError("");
  };

  const handleSave = () => {
    const normalized = normalizePaymentDraft(draft);
    const hasBank = hasBankPaymentDetails(normalized);
    const hasUpi = hasUpiPaymentDetails(normalized);
    const hasPartialBank =
      normalized.account_number ||
      normalized.ifsc_code ||
      normalized.account_holder_name;

    if (!hasBank && !hasUpi) {
      setError("Enter either full account details or a UPI ID.");
      return;
    }

    if (hasPartialBank && !hasBank) {
      setError(
        "Complete account number, IFSC code, and holder name, or use only UPI ID.",
      );
      return;
    }

    if (hasUpi && !UPI_ID_REGEX.test(normalized.upi_id)) {
      setError("Enter a valid UPI ID (for example: name@upi).");
      return;
    }

    onSave(normalized);
  };

  return (
    <CustomModal
      show={show}
      size="md"
      title="Online Payment Details"
      onClose={onClose}
    >
      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-black uppercase tracking-widest text-slate-700">
            Account Details
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Fill all three fields if you want to use bank transfer.
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Account Number
              </label>
              <input
                type="text"
                lang="en"
                translate="no"
                value={draft.account_number}
                onChange={(e) => setField("account_number", e.target.value)}
                className="no-translate w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                placeholder="Enter account number"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                IFSC Code
              </label>
              <input
                type="text"
                lang="en"
                translate="no"
                value={draft.ifsc_code}
                onChange={(e) => setField("ifsc_code", e.target.value)}
                className="no-translate w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                placeholder="Enter IFSC code"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-slate-700">
                Holder Name
              </label>
              <input
                type="text"
                lang="en"
                translate="no"
                value={draft.account_holder_name}
                onChange={(e) =>
                  setField("account_holder_name", e.target.value)
                }
                className="no-translate w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
                placeholder="Enter account holder name"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="h-px flex-1 bg-slate-200"></div>
          <span className="text-xs font-black uppercase tracking-widest text-slate-400">
            Or
          </span>
          <div className="h-px flex-1 bg-slate-200"></div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-black uppercase tracking-widest text-slate-700">
            UPI Details
          </p>
          <p className="mt-1 text-sm text-slate-500">
            Enter only a UPI ID if you do not want to use bank transfer.
          </p>
          <div className="mt-4">
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              UPI ID
            </label>
            <input
              type="text"
              lang="en"
              translate="no"
              value={draft.upi_id}
              onChange={(e) => setField("upi_id", e.target.value)}
              className="no-translate w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
              placeholder="example@upi"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="rounded-xl bg-[linear-gradient(135deg,#0f766e_0%,#155e75_100%)] px-5 py-2.5 text-sm font-bold text-white shadow-sm shadow-teal-200 transition hover:opacity-95"
          >
            Save Payment Details
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

const UpiVerificationModal = ({
  show,
  upiId,
  onClose,
  onConfirm,
}) => {
  const [qrCode, setQrCode] = useState("");
  const [qrError, setQrError] = useState("");

  const upiPaymentUri = buildUpiPaymentUri({
    upiId,
    note: "Event MOI",
  });

  useEffect(() => {
    if (!show) {
      setQrCode("");
      setQrError("");
      return;
    }

    if (!upiPaymentUri) {
      setQrCode("");
      setQrError("Unable to build the UPI QR for this ID.");
      return;
    }

    let isActive = true;
    setQrCode("");
    setQrError("");

    QRCode.toDataURL(upiPaymentUri, {
      width: 280,
      margin: 2,
      color: {
        dark: "#0f172a",
        light: "#ffffff",
      },
    })
      .then((dataUrl) => {
        if (!isActive) return;
        setQrCode(dataUrl);
      })
      .catch(() => {
        if (!isActive) return;
        setQrError("Unable to generate the UPI QR right now.");
      });

    return () => {
      isActive = false;
    };
  }, [show, upiPaymentUri]);

  return (
    <CustomModal show={show} onClose={onClose} title="Verify UPI QR" size="md">
      <div className="space-y-5">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-sm font-semibold text-amber-900">
            Please scan this QR once and confirm the UPI ID looks correct before
            creating the event.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-[1.1fr_0.9fr]">
          <div className="space-y-3">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-black uppercase tracking-widest text-slate-500">
                UPI ID
              </p>
              <p className="mt-1 break-all text-sm font-semibold text-slate-900">
                {upiId || "Not provided"}
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-center text-xs font-black uppercase tracking-widest text-slate-500">
              QR Preview
            </p>
            <div className="mt-4 flex min-h-[280px] items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50">
              {qrCode ? (
                <img
                  src={qrCode}
                  alt="UPI verification QR code"
                  className="h-64 w-64 rounded-2xl bg-white p-3 shadow-sm"
                />
              ) : (
                <p className="px-6 text-center text-sm text-slate-500">
                  {qrError || "Generating QR code..."}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Edit UPI Details
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!upiPaymentUri || Boolean(qrError)}
            className={`rounded-xl px-5 py-2.5 text-sm font-bold text-white transition ${
              !upiPaymentUri || qrError
                ? "cursor-not-allowed bg-slate-300"
                : "bg-[linear-gradient(135deg,#0f766e_0%,#155e75_100%)] shadow-sm shadow-teal-200 hover:opacity-95"
            }`}
          >
            I have verified it's correct QR
          </button>
        </div>
      </div>
    </CustomModal>
  );
};

const QuestionsPopup = ({ show, confirmSubmission }) => {
  // Kept for reference, but not used in the location dropdown
  const options = [
    { name: "React", id: 1 },
    { name: "Angular", id: 2 },
    { name: "Vue", id: 3 },
    { name: "Svelte", id: 4 },
  ];

  const [userLocationList, SetUserLocationList] = useState([]);
  const [audioDuration, setAudioDuration] = useState(null);
  const [audioCredits, setAudioCredits] = useState(0);
  const [matchedMobileCount, setMatchedMobileCount] = useState(0);
  const [isCheckingLocations, setIsCheckingLocations] = useState(false);
  const [locationMatchError, setLocationMatchError] = useState("");
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [showUpiVerificationModal, setShowUpiVerificationModal] =
    useState(false);
  const [paymentDetailsError, setPaymentDetailsError] = useState("");
  const pendingPaymentDraftRef = useRef(null);

  // Updated to use Formik's setFieldValue
  const onSelect = (selectedList, selectedItem) => {
    console.log("Selected:", selectedList);
    formik.setFieldValue("selectedLocations", selectedList);
  };

  // Updated to use Formik's setFieldValue
  const onRemove = (selectedList, removedItem) => {
    console.log("Removed:", selectedList);
    formik.setFieldValue("selectedLocations", selectedList);
  };

  useEffect(() => {
    (async () => {
      const locationList = await GetUserLocationList();
      if (locationList) {
        const formattedLocationlist = locationList.map((items) => {
          return { name: items, id: items };
        });
        console.log("formattedLocationlist", formattedLocationlist);
        SetUserLocationList(formattedLocationlist);
      }
    })();
  }, []);

  const formik = useFormik({
    initialValues: {
      reminderService: [],
      smsSchedule: null,
      voiceCallSchedule: null,
      voiceCallFile: null,
      hasCustomList: false, // NEW
      customListFile: null, // NEW
      selectedLocations: [], // NEW: For Multiselect
      moiConfirmation: "",
      onlinePayments: "",
      account_number: "",
      ifsc_code: "",
      account_holder_name: "",
      upi_id: "",
    },
    validationSchema: Yup.object({
      reminderService: Yup.array()
        .of(Yup.string())
        .test(
          "no-exclusive",
          "You cannot select other options if 'No' is selected.",
          (value) => !(value.includes("no") && value.length > 1),
        )
        .min(1, "Please select at least one option for reminder service.")
        .required("Please select at least one option for reminder service."),
      smsSchedule: Yup.date()
        .nullable()
        .when("reminderService", {
          is: (val) => val.includes("sms"),
          then: (schema) =>
            schema
              .min(new Date(), "Please select a future date and time")
              .required("SMS schedule is required."),
        }),
      voiceCallSchedule: Yup.date()
        .nullable()
        .when("reminderService", {
          is: (val) => val.includes("voiceCall"),
          then: (schema) =>
            schema
              .min(new Date(), "Please select a future date and time")
              .required("Voice Call schedule is required."),
        }),
      voiceCallFile: Yup.mixed()
        .nullable()
        .when("reminderService", {
          is: (val) => val.includes("voiceCall"),
          then: (schema) =>
            schema
              .required("Please upload a WAV file for the voice call.")
              .test(
                "file-format",
                "Only .wav files are allowed.",
                (value) =>
                  value &&
                  value.name &&
                  value.name.toLowerCase().endsWith(".wav"),
              )
              .test(
                "file-duration",
                "Voice media should be less than or equal to 60 seconds (1 minute).",
                (value) =>
                  new Promise((resolve) => {
                    if (!value) return resolve(true);
                    const audio = document.createElement("audio");
                    audio.src = URL.createObjectURL(value);
                    audio.addEventListener("loadedmetadata", () => {
                      resolve(audio.duration <= 60);
                    });
                    audio.addEventListener("error", () => resolve(false));
                  }),
              ),
        }),
      // Custom List/Location Validation (Updated)
      customListFile: Yup.mixed()
        .nullable()
        .when("hasCustomList", {
          is: true,
          then: (schema) =>
            schema
              .test(
                "custom-list-required",
                "Please upload an Excel file OR select at least one location.",
                function (value) {
                  // 'this.parent' gives access to all values in the formik form
                  const { selectedLocations } = this.parent;
                  // If 'hasCustomList' is true, either a file must be uploaded OR locations must be selected
                  if (
                    !value &&
                    (!selectedLocations || selectedLocations.length === 0)
                  ) {
                    return false; // Validation fails
                  }
                  return true; // Validation passes
                },
              )
              // Only check file format if a file is actually present
              .test(
                "file-format",
                "Only .xls or .xlsx files are allowed.",
                (value) => {
                  if (!value) return true; // Pass if no file is selected (validation is handled by 'custom-list-required')
                  return (
                    value.name &&
                    (value.name.toLowerCase().endsWith(".xls") ||
                      value.name.toLowerCase().endsWith(".xlsx"))
                  );
                },
              ),
          otherwise: (schema) => schema.nullable(), // Not required if unchecked
        }),
      // selectedLocations doesn't need explicit YUP validation if customListFile handles the dependency check
      moiConfirmation: Yup.string().required("Please select an option."),
      onlinePayments: Yup.string().required("Please select an option."),
    }),
    onSubmit: (values, { resetForm }) => {
      if (
        values.hasCustomList &&
        values.selectedLocations.length > 0 &&
        !values.customListFile &&
        matchedMobileCount === 0
      ) {
        setLocationMatchError(
          "Select a location with at least one matched mobile number or upload an Excel file.",
        );
        return;
      }

      if (
        values.onlinePayments === "yes" &&
        !hasBankPaymentDetails(values) &&
        !hasUpiPaymentDetails(values)
      ) {
        setPaymentDetailsError(
          "Add either full account details or a UPI ID for online payments.",
        );
        setShowPaymentDetailsModal(true);
        return;
      }

      // Calculate credits based on duration if voice call file exists
      let voiceCallCredits = 0;
      if (values.voiceCallFile && audioDuration !== null) {
        if (audioDuration <= 20) {
          voiceCallCredits = 1;
        } else if (audioDuration <= 40) {
          voiceCallCredits = 2;
        } else if (audioDuration <= 60) {
          voiceCallCredits = 3;
        }
      }

      const hasSmsReminder = values.reminderService.includes("sms");
      const hasVoiceReminder = values.reminderService.includes("voiceCall");

      const alteredValue = {
        reminder_sms: hasSmsReminder ? 1 : 0,
        reminder_voice: hasVoiceReminder ? 1 : 0,
        reminder_sms_date: hasSmsReminder && values.smsSchedule
          ? formatToDateTimeString(values.smsSchedule)
          : null,
        reminder_call_date: hasVoiceReminder && values.voiceCallSchedule
          ? formatToDateTimeString(values.voiceCallSchedule)
          : null,
        reminder_voice_id: hasVoiceReminder ? values.voiceCallFile : null,
        voice_call_credits: hasVoiceReminder ? voiceCallCredits : 0,
        has_custom_list: values.hasCustomList ? 1 : 0,
        custom_list_file: values.customListFile,
        // NEW: Add selected locations to the submission
        custom_list_locations: values.selectedLocations.map((item) => item.id),
        moi_confirmation_sms: values.moiConfirmation !== "no" ? 1 : 0,
        accept_online_payments: values.onlinePayments !== "no" ? 1 : 0,
        account_number:
          values.onlinePayments === "yes" ? values.account_number.trim() : null,
        ifsc_code:
          values.onlinePayments === "yes" ? values.ifsc_code.trim() : null,
        account_holder_name:
          values.onlinePayments === "yes"
            ? values.account_holder_name.trim()
            : null,
        upi_id: values.onlinePayments === "yes" ? values.upi_id.trim() : null,
        online_payment_details:
          values.onlinePayments === "yes"
            ? buildOnlinePaymentDetails(values)
            : null,
      };

      confirmSubmission(alteredValue, resetForm);
    },
  });

  useEffect(() => {
    if (
      !formik.values.hasCustomList ||
      formik.values.selectedLocations.length === 0
    ) {
      setMatchedMobileCount(0);
      setLocationMatchError("");
      setIsCheckingLocations(false);
      return;
    }

    let isActive = true;

    const fetchMobileCount = async () => {
      setIsCheckingLocations(true);
      setLocationMatchError("");

      const selectedLocationIds = formik.values.selectedLocations.map(
        (item) => item.id,
      );
      const mobileNumbers = await getMobileNumbersByCity(selectedLocationIds, {
        showToast: false,
      });

      if (!isActive) {
        return;
      }

      if (Array.isArray(mobileNumbers)) {
        setMatchedMobileCount(mobileNumbers.length);
        if (mobileNumbers.length === 0) {
          setLocationMatchError(
            "No mobile numbers were found for the selected locations.",
          );
        }
      } else {
        setMatchedMobileCount(0);
        setLocationMatchError(
          "We could not verify the mobile number count for the selected locations.",
        );
      }

      setIsCheckingLocations(false);
    };

    fetchMobileCount();

    return () => {
      isActive = false;
    };
  }, [formik.values.hasCustomList, formik.values.selectedLocations]);

  const now = new Date();

  const getMinDate = () => {
    return now.getHours() >= 18 ? addDays(now, 1) : now;
  };

  const getVoiceCallMinTime = () => {
    if (
      formik.values.voiceCallSchedule &&
      isSameDay(formik.values.voiceCallSchedule, now)
    ) {
      return now;
    }
    // Use the selected date as the base, defaulting to today if not set
    const baseDate = formik.values.voiceCallSchedule || new Date();
    return setHours(setMinutes(new Date(baseDate), 0), 9);
  };

  const getVoiceCallMaxTime = () => {
    const baseDate = formik.values.voiceCallSchedule || new Date();
    return setHours(setMinutes(new Date(baseDate), 0), 18);
  };

  const getSmsMinTime = () => {
    if (
      formik.values.smsSchedule &&
      isSameDay(formik.values.smsSchedule, now)
    ) {
      return now;
    }
    // Use the selected date as the base, defaulting to today if not set
    const baseDate = formik.values.smsSchedule || new Date();
    return setHours(setMinutes(new Date(baseDate), 0), 9);
  };

  const getSmsMaxTime = () => {
    const baseDate = formik.values.smsSchedule || new Date();
    return setHours(setMinutes(new Date(baseDate), 0), 18);
  };

  // Function to calculate credits based on duration
  const calculateCredits = (duration) => {
    if (duration <= 20) return 1;
    if (duration <= 40) return 2;
    if (duration <= 60) return 3;
    return 0;
  };

  // Function to handle file selection and calculate duration/credits
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    formik.setFieldValue("voiceCallFile", file);

    if (file) {
      const duration = await new Promise((resolve) => {
        const audio = document.createElement("audio");
        audio.src = URL.createObjectURL(file);
        audio.addEventListener("loadedmetadata", () => {
          const dur = audio.duration;
          resolve(dur);
          URL.revokeObjectURL(audio.src);
        });
        audio.addEventListener("error", () => {
          resolve(null);
          URL.revokeObjectURL(audio.src);
        });
      });

      if (duration !== null) {
        setAudioDuration(duration);
        const credits = calculateCredits(duration);
        setAudioCredits(credits);
      } else {
        setAudioDuration(null);
        setAudioCredits(0);
      }
    } else {
      setAudioDuration(null);
      setAudioCredits(0);
    }

    formik.setFieldTouched("voiceCallFile", true, false);
  };

  if (!show) return null;

  const showVoiceCallPicker =
    formik.values.reminderService.includes("voiceCall");
  const showSmsPicker = formik.values.reminderService.includes("sms");
  const showCustomList = showVoiceCallPicker || showSmsPicker;
  const sectionCardCls =
    "space-y-4 rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-sm shadow-slate-200/60";
  const optionCardCls = (active) =>
    `flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-all ${
      active
        ? "border-teal-500 bg-teal-50 text-slate-900 shadow-sm shadow-teal-100"
        : "border-slate-200 bg-slate-50/70 text-slate-700 hover:border-teal-300 hover:bg-teal-50/60"
    }`;
  const fieldCls =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-800 outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/15 no-translate";
  const fileCls =
    "w-full cursor-pointer rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-700 outline-none transition hover:border-teal-400 hover:bg-teal-50/40 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/15";
  const errorCls =
    "flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700";

  const clearPaymentDetails = () => {
    formik.setFieldValue("account_number", "");
    formik.setFieldValue("ifsc_code", "");
    formik.setFieldValue("account_holder_name", "");
    formik.setFieldValue("upi_id", "");
    setPaymentDetailsError("");
    setShowPaymentDetailsModal(false);
  };

  const paymentDetailsAdded =
    hasBankPaymentDetails(formik.values) || hasUpiPaymentDetails(formik.values);

  const handleReminderServiceChange = (event) => {
    const { value, checked } = event.target;
    let updated = [...formik.values.reminderService];

    if (checked) {
      if (value === "no") {
        updated = ["no"];
        formik.setFieldValue("smsSchedule", null);
        formik.setFieldValue("voiceCallSchedule", null);
        formik.setFieldValue("voiceCallFile", null);
        setAudioDuration(null);
        setAudioCredits(0);
        formik.setFieldValue("hasCustomList", false);
        formik.setFieldValue("customListFile", null);
        formik.setFieldValue("selectedLocations", []);
      } else {
        updated = updated.filter((item) => item !== "no");
        updated.push(value);
      }
    } else {
      updated = updated.filter((item) => item !== value);
    }

    formik.setFieldValue("reminderService", updated);

    if (value === "sms" && !checked) {
      formik.setFieldValue("smsSchedule", null);
    }
    if (value === "voiceCall" && !checked) {
      formik.setFieldValue("voiceCallSchedule", null);
      formik.setFieldValue("voiceCallFile", null);
      setAudioDuration(null);
      setAudioCredits(0);
    }
  };

  // Function to download Excel template
  const downloadExcelTemplate = () => {
    const emptyData = [
      {
        "Mobile Number": "",
        Name: "",
      },
      {
        "Mobile Number": "",
        Name: "",
      },
      {
        "Mobile Number": "",
        Name: "",
      },
      {
        "Mobile Number": "",
        Name: "",
      },
      {
        "Mobile Number": "",
        Name: "",
      },
      {
        "Mobile Number": "",
        Name: "",
      },
      {
        "Mobile Number": "",
        Name: "",
      },
      {
        "Mobile Number": "",
        Name: "",
      },
      {
        "Mobile Number": "",
        Name: "",
      },
      {
        "Mobile Number": "",
        Name: "",
      },
    ];

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Template sheet with empty rows
    const templateSheet = XLSX.utils.json_to_sheet(emptyData);
    XLSX.utils.book_append_sheet(workbook, templateSheet, "Template");

    // Set column widths
    templateSheet["!cols"] = [
      { wch: 15 }, // Mobile Number
      { wch: 20 }, // Name
    ];

    // Download file
    XLSX.writeFile(workbook, "custom-list-template.xlsx");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl border border-white/20 bg-[linear-gradient(180deg,#f8fbfd_0%,#eef4f8_100%)] shadow-2xl shadow-slate-900/20">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-white/10 bg-[linear-gradient(135deg,#0f172a_0%,#16324b_48%,#0f766e_100%)] px-8 py-6 text-white">
          <h2 className="text-2xl font-black tracking-tight">
            Event Features & Settings
          </h2>
          <p className="mt-1 text-sm text-slate-200/85">
            Configure reminder services and preferences
          </p>
        </div>

        <form onSubmit={formik.handleSubmit} className="space-y-6 p-8">
          {/* Reminder Service Section */}
          <div className={sectionCardCls}>
            <label className="block text-base font-bold text-slate-900">
              <span className="flex items-center gap-2">
                📣 Reminder Service
              </span>
              <p className="mt-1 text-sm font-normal text-slate-500">
                Would you like to enable reminder notifications for your event?
              </p>
            </label>
            <div className="grid grid-cols-2 gap-4 md:flex md:flex-wrap">
              {["voiceCall", "sms", "no"].map((value) => (
                <label
                  key={value}
                  className={optionCardCls(
                    formik.values.reminderService.includes(value),
                  )}
                >
                  <input
                    type="checkbox"
                    name="reminderService"
                    value={value}
                    onChange={handleReminderServiceChange}
                    checked={formik.values.reminderService.includes(value)}
                    className="h-5 w-5 cursor-pointer accent-teal-600"
                  />
                  <span className="font-semibold">
                    {value === "voiceCall"
                      ? "Voice Call"
                      : value === "sms"
                        ? "SMS"
                        : "No Reminders"}
                  </span>
                </label>
              ))}
            </div>
            {formik.touched.reminderService &&
              formik.errors.reminderService && (
                <div className={errorCls}>
                  <span className="text-red-600 font-semibold">⚠</span>
                  <p className="text-red-700 text-sm">
                    {formik.errors.reminderService}
                  </p>
                </div>
              )}
          </div>

          {/* Voice Call Section */}
          {showVoiceCallPicker && (
            <div className={sectionCardCls}>
              <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                📞 Voice Call Configuration
              </h3>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Schedule Voice Call
                </label>
                <DatePicker
                  selected={formik.values.voiceCallSchedule}
                  onChange={(date) =>
                    formik.setFieldValue("voiceCallSchedule", date)
                  }
                  showTimeSelect
                  locale={enIN}
                  dateFormat="dd/MM/yyyy h:mm aa"
                  className={fieldCls}
                  placeholderText="Select date and time"
                  minDate={getMinDate()}
                  minTime={getVoiceCallMinTime()}
                  maxTime={getVoiceCallMaxTime()}
                />
                <p className="text-xs text-slate-500">Available 9 AM - 6 PM</p>
                {formik.touched.voiceCallSchedule &&
                  formik.errors.voiceCallSchedule && (
                    <div className={errorCls}>
                      <span className="text-red-600">⚠</span>
                      <p className="text-red-700 text-sm">
                        {formik.errors.voiceCallSchedule}
                      </p>
                    </div>
                  )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  🎶 Upload Voice File (WAV format)
                </label>
                <div className="relative">
                  <input
                    type="file"
                    accept=".wav"
                    onChange={handleFileChange}
                    className={fileCls}
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Only WAV format files are allowed. Maximum duration: 60
                    seconds (1 minute). Credits will be charged based on
                    duration:
                  </p>
                  <ul className="list-inside list-disc text-xs text-slate-500">
                    <li>Up to 20 seconds: 1 credit</li>
                    <li>20-40 seconds: 2 credits</li>
                    <li>40-60 seconds: 3 credits</li>
                  </ul>
                </div>
                {formik.values.voiceCallFile && (
                  <div className="mt-2 rounded-xl border border-teal-200 bg-teal-50/80 p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">
                          File: {formik.values.voiceCallFile.name}
                        </p>
                        {audioDuration !== null && (
                          <>
                            <p className="text-sm text-slate-600">
                              Duration: {audioDuration.toFixed(1)} seconds
                            </p>
                            <p className="text-sm text-slate-600">
                              Credits Required: {audioCredits}
                            </p>
                          </>
                        )}
                        {audioDuration === null && (
                          <p className="text-sm text-amber-600">
                            Analyzing audio file...
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        {audioDuration !== null && (
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              audioCredits === 1
                                ? "bg-emerald-100 text-emerald-700"
                                : audioCredits === 2
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-rose-100 text-rose-700"
                            }`}
                          >
                            {audioCredits} Credit{audioCredits > 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                {formik.touched.voiceCallFile &&
                  formik.errors.voiceCallFile && (
                    <div className={errorCls}>
                      <span className="text-red-600">⚠</span>
                      <p className="text-red-700 text-sm">
                        {formik.errors.voiceCallFile}
                      </p>
                    </div>
                  )}
                <a
                  href="https://voicemaker.in/"
                  className="mt-2 flex items-center gap-1 text-sm font-semibold text-teal-700 transition-colors hover:text-teal-800"
                  rel="noreferrer"
                  target="_blank"
                >
                  🔗 Create AI Voice
                </a>
              </div>
            </div>
          )}

          {/* SMS Section */}
          {showSmsPicker && (
            <div className={sectionCardCls}>
              <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                📧 SMS Reminder Configuration
              </h3>
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-slate-700">
                  Schedule SMS Reminder
                </label>
                <DatePicker
                  selected={formik.values.smsSchedule}
                  onChange={(date) => formik.setFieldValue("smsSchedule", date)}
                  showTimeSelect
                  locale={enIN}
                  dateFormat="dd/MM/yyyy h:mm aa"
                  className={fieldCls}
                  placeholderText="Select date and time"
                  minDate={getMinDate()}
                  minTime={getSmsMinTime()}
                  maxTime={getSmsMaxTime()}
                />
                <p className="text-xs text-slate-500">Available 9 AM - 6 PM</p>
                {formik.touched.smsSchedule && formik.errors.smsSchedule && (
                  <div className={errorCls}>
                    <span className="text-red-600">⚠</span>
                    <p className="text-red-700 text-sm">
                      {formik.errors.smsSchedule}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Custom List Section */}
          {showCustomList && (
            <div className={sectionCardCls}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="hasCustomList"
                  checked={formik.values.hasCustomList}
                  onChange={(e) => {
                    formik.setFieldValue("hasCustomList", e.target.checked);
                    if (!e.target.checked) {
                      formik.setFieldValue("customListFile", null);
                      formik.setFieldValue("selectedLocations", []);
                    }
                  }}
                  className="h-5 w-5 cursor-pointer accent-teal-600"
                />
                <span className="text-base font-bold text-slate-900">
                  📍 Use Custom List for Reminders?
                </span>
              </label>

              {formik.touched.customListFile &&
                formik.errors.customListFile && (
                  <div className={errorCls}>
                    <span className="text-red-600 font-semibold">⚠</span>
                    <p className="text-red-700 text-sm">
                      {formik.errors.customListFile}
                    </p>
                  </div>
                )}

              {formik.values.hasCustomList && (
                <div className="mt-4 space-y-6">
                  <div className="space-y-2">
                    <p className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-xs text-white">
                        1
                      </span>
                      Upload Excel List
                    </p>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={downloadExcelTemplate}
                        className="flex items-center gap-2 rounded-xl bg-[linear-gradient(135deg,#0f766e_0%,#155e75_100%)] px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-teal-200 transition hover:opacity-90"
                      >
                        📥 Download Template
                      </button>
                      <p className="flex items-center text-xs text-slate-500">
                        Click to download an Excel template with examples
                      </p>
                    </div>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={(event) =>
                          formik.setFieldValue(
                            "customListFile",
                            event.target.files[0],
                          )
                        }
                        className={fileCls}
                      />
                      <p className="mt-1 text-xs text-slate-500">
                        Supported formats: .xls, .xlsx
                      </p>
                      {formik.values.customListFile && (
                        <p className="mt-1 text-xs font-semibold text-emerald-600">
                          ✓ File selected: {formik.values.customListFile.name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="flex-1 border-t border-slate-300"></div>
                    <span className="px-2 text-sm font-semibold text-slate-500">
                      OR
                    </span>
                    <div className="flex-1 border-t border-slate-300"></div>
                  </div>

                  <div className="space-y-2">
                    <p className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-teal-600 text-xs text-white">
                        2
                      </span>
                      Select Cities/Locations
                    </p>
                    <Multiselect
                      options={userLocationList}
                      selectedValues={formik.values.selectedLocations}
                      onSelect={onSelect}
                      onRemove={onRemove}
                      displayValue="name"
                      placeholder="Search and select locations"
                      className={`w-full ${
                        formik.touched.customListFile &&
                        formik.errors.customListFile &&
                        !formik.values.customListFile &&
                        !formik.values.selectedLocations.length
                          ? "border-red-500"
                          : ""
                      }`}
                      style={{
                        chips: {
                          background: "#0f766e",
                        },
                        multiselectContainer: {
                          color: "#334155",
                        },
                        searchBox: {
                          border: "1px solid #cbd5e1",
                          borderRadius: "0.75rem",
                          padding: "0.75rem",
                          background: "#f8fafc",
                        },
                      }}
                    />
                    {formik.values.selectedLocations.length > 0 && (
                      <div className="mt-3 space-y-3 rounded-xl border border-teal-200 bg-teal-50/70 p-4">
                        <p className="text-sm font-semibold text-slate-800">
                          Selected Locations:{" "}
                          {formik.values.selectedLocations
                            .map((location) => location.name)
                            .join(", ")}
                        </p>
                        <div className="rounded-xl bg-white px-4 py-3">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Matched Mobile Numbers
                          </p>
                          <p className="mt-1 text-2xl font-black text-slate-900">
                            {isCheckingLocations
                              ? "Checking..."
                              : matchedMobileCount}
                          </p>
                        </div>
                        {!isCheckingLocations && matchedMobileCount > 0 && (
                          <p className="text-sm text-emerald-700">
                            These matched mobile numbers will be added to
                            `event_automation_list` for this event after submit.
                          </p>
                        )}
                        {locationMatchError && (
                          <p className="text-sm text-red-700">
                            {locationMatchError}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Moi Confirmation Section */}
          <div className={sectionCardCls}>
            <label className="block text-base font-bold text-slate-900">
              <span className="flex items-center gap-2">
                💬 Moi Confirmation Message
              </span>
              <p className="mt-1 text-sm font-normal text-slate-500">
                Send a confirmation message when MOI is received?
              </p>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {["yes", "no"].map((value) => (
                <label
                  key={value}
                  className={optionCardCls(formik.values.moiConfirmation === value)}
                >
                  <input
                    type="radio"
                    name="moiConfirmation"
                    value={value}
                    onChange={formik.handleChange}
                    checked={formik.values.moiConfirmation === value}
                    className="h-5 w-5 cursor-pointer accent-teal-600"
                  />
                  <span className="font-semibold">
                    {value === "yes" ? "✓ Yes" : "✗ No"}
                  </span>
                </label>
              ))}
            </div>
            {formik.touched.moiConfirmation &&
              formik.errors.moiConfirmation && (
                <div className={errorCls}>
                  <span className="text-red-600">⚠</span>
                  <p className="text-red-700 text-sm">
                    {formik.errors.moiConfirmation}
                  </p>
                </div>
              )}
          </div>

          {/* Online Payments Section */}
          <div className={sectionCardCls}>
            <label className="block text-base font-bold text-slate-900">
              <span className="flex items-center gap-2">
                💳 Online Payment Support
              </span>
              <p className="mt-1 text-sm font-normal text-slate-500">
                Does your event accept online payments for MOI?
              </p>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {["yes", "no"].map((value) => (
                <label
                  key={value}
                  className={optionCardCls(formik.values.onlinePayments === value)}
                >
                  <input
                    type="radio"
                    name="onlinePayments"
                    value={value}
                    onChange={(e) => {
                      formik.handleChange(e);
                      setPaymentDetailsError("");
                      if (e.target.value === "yes") {
                        setShowPaymentDetailsModal(true);
                      }
                      if (e.target.value === "no") {
                        clearPaymentDetails();
                      }
                    }}
                    checked={formik.values.onlinePayments === value}
                    className="h-5 w-5 cursor-pointer accent-teal-600"
                  />
                  <span className="font-semibold">
                    {value === "yes" ? "✓ Yes" : "✗ No"}
                  </span>
                </label>
              ))}
            </div>
            {formik.touched.onlinePayments && formik.errors.onlinePayments && (
              <div className={errorCls}>
                <span className="text-red-600">⚠</span>
                <p className="text-red-700 text-sm">
                  {formik.errors.onlinePayments}
                </p>
              </div>
            )}
            {formik.values.onlinePayments === "yes" && (
              <div className="mt-4 space-y-3">
                <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50/80 p-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-sm font-bold text-slate-900">
                      Payment details
                    </p>
                    <p className="mt-1 text-sm text-slate-500">
                      Add either full account details or only a UPI ID.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPaymentDetailsError("");
                      setShowPaymentDetailsModal(true);
                    }}
                    className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-teal-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-teal-50"
                  >
                    {paymentDetailsAdded
                      ? "Edit Payment Details"
                      : "Add Payment Details"}
                  </button>
                </div>

                {paymentDetailsAdded && (
                  <div className="rounded-2xl border border-teal-200 bg-teal-50/70 p-4">
                    {hasBankPaymentDetails(formik.values) && (
                      <div className="space-y-1">
                        <p className="text-xs font-black uppercase tracking-widest text-teal-700">
                          Account Details
                        </p>
                        <p className="text-sm text-slate-700">
                          Holder: {formik.values.account_holder_name}
                        </p>
                        <p className="text-sm text-slate-700">
                          Account Number: {formik.values.account_number}
                        </p>
                        <p className="text-sm text-slate-700">
                          IFSC Code: {formik.values.ifsc_code}
                        </p>
                      </div>
                    )}
                    {hasUpiPaymentDetails(formik.values) && (
                      <div
                        className={
                          hasBankPaymentDetails(formik.values) ? "mt-4" : ""
                        }
                      >
                        <p className="text-xs font-black uppercase tracking-widest text-teal-700">
                          UPI Details
                        </p>
                        <p className="mt-1 text-sm text-slate-700">
                          UPI ID: {formik.values.upi_id}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {paymentDetailsError && (
                  <div className={errorCls}>
                    <span className="text-red-600">âš </span>
                    <p className="text-red-700 text-sm">
                      {paymentDetailsError}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 border-t border-slate-200 pt-2">
            <button
              type="submit"
              className="flex-1 rounded-xl bg-[linear-gradient(135deg,#0f766e_0%,#155e75_100%)] px-6 py-3 font-bold text-white shadow-lg shadow-teal-200 transition hover:opacity-95"
            >
              ✓ Continue & Create Event
            </button>
          </div>
        </form>
      </div>

      <PaymentDetailsModal
        show={showPaymentDetailsModal}
        initialValues={formik.values}
        onClose={() => setShowPaymentDetailsModal(false)}
        onSave={(draft) => {
          if (draft.upi_id) {
            pendingPaymentDraftRef.current = draft;
            setShowUpiVerificationModal(true);
            return;
          }

          formik.setFieldValue("account_number", draft.account_number);
          formik.setFieldValue("ifsc_code", draft.ifsc_code);
          formik.setFieldValue(
            "account_holder_name",
            draft.account_holder_name,
          );
          formik.setFieldValue("upi_id", draft.upi_id);
          setPaymentDetailsError("");
          setShowPaymentDetailsModal(false);
        }}
      />
      <UpiVerificationModal
        show={showUpiVerificationModal}
        upiId={pendingPaymentDraftRef.current?.upi_id || ""}
        onClose={() => {
          setShowUpiVerificationModal(false);
          pendingPaymentDraftRef.current = null;
        }}
        onConfirm={() => {
          const pendingDraft = pendingPaymentDraftRef.current;
          if (!pendingDraft) return;
          formik.setFieldValue("account_number", pendingDraft.account_number);
          formik.setFieldValue("ifsc_code", pendingDraft.ifsc_code);
          formik.setFieldValue(
            "account_holder_name",
            pendingDraft.account_holder_name,
          );
          formik.setFieldValue("upi_id", pendingDraft.upi_id);
          setPaymentDetailsError("");
          setShowPaymentDetailsModal(false);
          setShowUpiVerificationModal(false);
          pendingPaymentDraftRef.current = null;
        }}
      />
    </div>
  );
};

export default QuestionsPopup;
